import {
  IActionRepeaterValue,
  MAKYO_STORAGE_PATH,
  TypographyGroupValue,
  VariableSelectFeatureFlag,
} from '@makyoapp/makyo-common'
import { AspectRatio, CornerRadius, HoverEffectVariant, Ratio, RawSpacingInput } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { THUMB_SQUARE } from '@/shared/constants'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'

import { convertRatioFormat } from '../../utils'
import { CompImageProps } from './comp'

type ImageDefaultLocal = {
  isLocal: boolean
  value: string
}

function isLocalImage(imageSrc: unknown): imageSrc is ImageDefaultLocal {
  return typeof imageSrc === 'object' && imageSrc !== null && 'isLocal' in imageSrc
}

function cleanLocalImageSrc(localImageSrc: string) {
  // the src might be:
  // /src/assets/img/default_thumbnail_2.png, or
  // /assets/img/default_thumbnail_2.png
  // to fetch from public url, we need to provide the path by removing src/
  return localImageSrc.replace('src/', '')
}

function getImageSrc(imageSrc: string | ImageDefaultLocal | undefined) {
  if (!imageSrc) return ''
  if (isLocalImage(imageSrc)) {
    return cleanLocalImageSrc(imageSrc.value)
  }
  if (imageSrc.includes(MAKYO_STORAGE_PATH)) {
    return imageSrc
  }
  return imageSrc
}

export interface CompImageWrapperCoreProps {
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: CompImageProps) => React.ReactNode
}

/**
 * CompImageWrapperCore
 */
export function CompImageWrapperCore({
  infos: { component, settings, containerClient, detailTableId, detailRowIndex, currentParentBg },
  action: { simulateAction },
  children,
}: CompImageWrapperCoreProps) {
  const { isDevelopmentMode, openComponentSettings } = useAppBuilderContext()

  // ========================= data =========================
  const rawImageSrc = settings[ComponentSettingKey.ImageDataSrc]

  const isPageCover = rawImageSrc?.flag === VariableSelectFeatureFlag.PageCover

  const imageSrc = useParseVariableSelect<string>(
    settings[ComponentSettingKey.ImageDataSrc],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const parsedImageSrc = getImageSrc(imageSrc)

  const coverSetting = (containerClient as any)?.settings?._CoverImage
  const pageCoverSrc =
    typeof coverSetting === 'string'
      ? coverSetting
      : coverSetting && typeof coverSetting === 'object'
        ? (coverSetting.value ?? '')
        : ''

  const realSrc = isPageCover
    ? getImageSrc(pageCoverSrc)
    : isLocalImage(rawImageSrc)
      ? cleanLocalImageSrc(rawImageSrc.value)
      : parsedImageSrc

  const hasSrc = !!realSrc.trim().length

  // image title
  const title = useParseVariableText(
    settings[ComponentSettingKey.ImageDataTitle],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  // image tag
  const tag = useParseVariableText(
    settings[ComponentSettingKey.ImageDataTag],
    containerClient,
    detailTableId,
    detailRowIndex
  )
  const withTag = !isEmpty(tag) && hasSrc

  // image like
  const like = useParseVariableSelect<string>(
    settings[ComponentSettingKey.ImageDataLike],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const liked = like?.toLowerCase() === 'true'
  const withLike = !isEmpty(like) && hasSrc

  // Handle image loading timeout
  // const { setErr, imgRef } = useImgTimeout(parsedImageSrc)

  // ========================= design =========================

  // base
  const width = getSafely(settings, [ComponentSettingKey.ImageDesignWidth], ['100%'])[0]
  const alignment = settings?.[ComponentSettingKey.ImageDesignHAlign] ?? 'parent'
  const margin = settings[ComponentSettingKey.ImageDesignMargin] as RawSpacingInput

  // const compTitleAlign = settings?.[ComponentSettingKey.ImageDesignCompTitleAlign] as 'start' | 'center' | 'end'
  const textLineCaption = settings?.[ComponentSettingKey.ImageDesignTextLineCaption] as number

  // Process border
  const border = (settings?.[ComponentSettingKey.ImageDesignBorder] as number[])?.[0]
  const borderColor = settings?.[ComponentSettingKey.ImageDesignBorderColor]
  const badgeColor = settings?.[ComponentSettingKey.ImageDesignBadgeColor]
  const backgroundCaption = settings?.[ComponentSettingKey.ImageDesignBackgroundCaption]

  // image
  const cornerRadius = settings?.[ComponentSettingKey.ImageDesignShape] as CornerRadius
  const likeIcon = (settings?.[ComponentSettingKey.ImageDesignLikeIcon] as string) ?? 'favorite'

  // typography
  const typoSettings = (settings[ComponentSettingKey.ImageDesignTypography] || {}) as
    | TypographyGroupValue
    | Record<string, any>
  const captionTypography = (typoSettings as any)?.[ComponentSettingKey.ImageDesignCaptionTypography] || {
    typography: 'body-medium',
  }
  const tagTypography = (typoSettings as any)?.[ComponentSettingKey.ImageDesignTagTypography] || {
    typography: 'label-medium',
  }

  // Process aspect ratio and shape
  const aspectRatio = settings?.[ComponentSettingKey.ImageDesignRatio] as AspectRatio

  const hoverEffect = settings?.[ComponentSettingKey.ImageDesignHoverEffect] as HoverEffectVariant

  // ========================= action =========================
  // Process click actions
  const imageClickActions = getSafely(settings, [ComponentSettingKey.ImageActionClick], []) as IActionRepeaterValue[]
  const imageLikeActions = getSafely(settings, [ComponentSettingKey.ImageActionLikeClick], []) as IActionRepeaterValue[]

  // Handle click events
  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
    if (imageClickActions && imageClickActions.length > 0) {
      simulateAction({
        actions: imageClickActions,
      })
    }
  }

  const handleLikeClick = () => {
    if (imageLikeActions && imageLikeActions.length > 0) {
      simulateAction({
        actions: imageLikeActions,
      })
    }
  }

  const shouldShow = hasSrc
  if (!shouldShow) return null

  const imageProps: CompImageProps = {
    // base
    id: component._id,
    width,
    alignment,
    margin,
    backgroundColor: currentParentBg,

    // image
    src: realSrc ?? THUMB_SQUARE,
    caption: title,
    tag: withTag ? tag : undefined,
    tagColor: badgeColor,
    withLike,
    liked,
    likeIcon,
    textLineCaption,
    ratio: convertRatioFormat(aspectRatio) as Ratio,
    cornerRadius,
    borderWidth: border ? (typeof border === 'number' ? `${border}px` : border) : undefined,
    borderColor,
    captionTypography,
    tagTypography,
    onLikeClick: handleLikeClick,
    onClick: handleClick,
    devMode: isDevelopmentMode,
    hoverEffect,
    hoverIntensity: 'md',
  }

  return children(imageProps)
}
